// Flood / simulation scenario picker. The flood-model rasters are a grid of scenarios
// (return period x year x variable); rather than dumping ~80 rasters into the 2D tree,
// this shows ONE scenario at a time via three dropdowns. Hidden if the project has none.
import { api, type FloodScenario } from "../api";
import type { AssetRef, LayerManager } from "../cesium/layers";
import { $activeProjectId } from "../store";

export function mountFloodPanel(
  container: HTMLElement,
  layers: LayerManager,
): { refresh: () => Promise<void> } {
  // A real AssetRef from a scenario: it MUST carry the tile URL + bbox or the imagery
  // provider has nothing to fetch (the bug where the panel showed but no raster rendered).
  const ref = (s: FloodScenario): AssetRef => ({
    id: s.asset_id,
    kind: "raster_tiles",
    dimension: "2D",
    web_path: s.web_path,
    bbox: s.bbox,
  });

  function labelledSelect(label: string, values: (string | number)[], fmt: (v: string | number) => string) {
    const wrap = document.createElement("label");
    wrap.className = "flood-field";
    wrap.append(Object.assign(document.createElement("span"), { textContent: label }));
    const sel = document.createElement("select");
    for (const v of values) {
      const o = document.createElement("option");
      o.value = String(v);
      o.textContent = fmt(v);
      sel.append(o);
    }
    wrap.append(sel);
    return { wrap, sel };
  }

  let shown: FloodScenario | null = null;

  async function refresh(): Promise<void> {
    const section = container.closest(".section") as HTMLElement | null;
    container.innerHTML = "";
    shown = null;
    const pid = $activeProjectId.get();
    if (pid == null) return;

    let data;
    try {
      data = await api.floodScenarios(pid);
    } catch {
      if (section) section.style.display = "none";
      return;
    }
    const scenarios = data.scenarios;
    if (!scenarios.length) {
      if (section) section.style.display = "none";
      return;
    }
    if (section) section.style.display = "";

    const rp = labelledSelect("Return period", data.return_periods, (v) => `${v}% AEP`);
    const yr = labelledSelect("Year", data.years, (v) => String(v));
    const va = labelledSelect("Variable", data.variables, (v) => String(v));

    const apply = async (): Promise<void> => {
      const s = scenarios.find(
        (x: FloodScenario) =>
          x.return_period === Number(rp.sel.value) &&
          x.year === Number(yr.sel.value) &&
          x.variable === va.sel.value,
      );
      if (shown && shown.asset_id !== s?.asset_id) await layers.setVisible(ref(shown), false);
      shown = s ?? null;
      if (s) await layers.setVisible(ref(s), true);
    };
    for (const f of [rp, yr, va]) f.sel.addEventListener("change", () => void apply());

    const show = document.createElement("button");
    show.className = "tool-btn";
    show.textContent = "Show scenario";
    show.addEventListener("click", () => void apply());

    const off = document.createElement("button");
    off.className = "tool-btn";
    off.textContent = "Hide";
    off.addEventListener("click", async () => {
      if (shown) await layers.setVisible(ref(shown), false);
      shown = null;
    });

    const hint = document.createElement("div");
    hint.className = "hint";
    hint.textContent = "Flood model: pick a scenario to show one depth/level/velocity grid at a time.";

    const actions = document.createElement("div");
    actions.className = "flood-actions";
    actions.append(show, off);
    container.append(hint, rp.wrap, yr.wrap, va.wrap, actions);
  }

  return { refresh };
}
